import React, { useState } from 'react';
import {
    TierVariant,
    PricingTier,
    NationalityType
} from '../../types/tieredPricing';
import { PlusIcon, XMarkIcon, CurrencyDollarIcon, TruckIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { transportService } from '../../services/transportService';
import { guideService } from '../../services/guideService';
import { TransportOption, GuideRanking } from '../../types/logistics';

interface TierEditorProps {
    variants: { [key in PricingTier]?: TierVariant };
    onChange: (variants: { [key in PricingTier]?: TierVariant }) => void;
}

const DEFAULT_VARIANT: TierVariant = {
    itinerary: [],
    inclusions: [],
    exclusions: [],
    guideClass: 'Standard',
    vehicleType: 'Safari Minivan',
    accommodationPools: [],
    baseServiceCosts: {
        [NationalityType.LOCAL]: { adult: 0, child: 0 },
        [NationalityType.INTERNATIONAL]: { adult: 0, child: 0 }
    }
};

export const TierEditor: React.FC<TierEditorProps> = ({ variants = {}, onChange }) => {
    const [activeTier, setActiveTier] = useState<PricingTier>(PricingTier.STANDARD);
    const [transportOptions, setTransportOptions] = useState<TransportOption[]>([]);
    const [guideRankings, setGuideRankings] = useState<GuideRanking[]>([]);
    const [loadingLogistics, setLoadingLogistics] = useState(true);

    React.useEffect(() => {
        const loadLogistics = async () => {
            setLoadingLogistics(true);
            try {
                const [transport, rankings] = await Promise.all([
                    transportService.getTransportOptions(),
                    guideService.getGuideRankings()
                ]);
                setTransportOptions(transport);
                setGuideRankings(rankings);
            } catch (error) {
                console.error('Error loading logistics:', error);
            } finally {
                setLoadingLogistics(false);
            }
        };
        loadLogistics();
    }, []);

    const currentVariant = variants[activeTier] || DEFAULT_VARIANT;

    const updateVariant = (updated: TierVariant) => {
        onChange({
            ...variants,
            [activeTier]: updated
        });
    };

    const handleCostChange = (
        nationality: NationalityType,
        type: 'adult' | 'child',
        value: string
    ) => {
        const numValue = parseFloat(value) || 0;
        updateVariant({
            ...currentVariant,
            baseServiceCosts: {
                ...currentVariant.baseServiceCosts,
                [nationality]: {
                    ...currentVariant.baseServiceCosts[nationality],
                    [type]: numValue
                }
            }
        });
    };

    const handleInputChange = (field: keyof TierVariant, value: any) => {
        updateVariant({
            ...currentVariant,
            [field]: value
        });
    };

    const handleArrayChange = (
        field: 'inclusions' | 'exclusions',
        index: number,
        value: string
    ) => {
        const newArray = [...currentVariant[field]];
        newArray[index] = value;
        updateVariant({
            ...currentVariant,
            [field]: newArray
        });
    };

    const addArrayItem = (field: 'inclusions' | 'exclusions') => {
        updateVariant({
            ...currentVariant,
            [field]: [...currentVariant[field], '']
        });
    };

    const removeArrayItem = (field: 'inclusions' | 'exclusions', index: number) => {
        const newArray = currentVariant[field].filter((_, i) => i !== index);
        updateVariant({
            ...currentVariant,
            [field]: newArray
        });
    };

    return (
        <div className="space-y-6 bg-gray-50 p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Tier Pricing Configuration</h3>
                <div className="flex space-x-2 bg-white p-1 rounded-lg border border-gray-300">
                    {[PricingTier.BUDGET, PricingTier.STANDARD, PricingTier.LUXURY].map((tier) => (
                        <button
                            key={tier}
                            type="button"
                            onClick={() => setActiveTier(tier)}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${activeTier === tier
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-gray-600 hover:bg-gray-100'
                                }`}
                        >
                            {tier}
                        </button>
                    ))}
                </div>
            </div>

            <div className="animate-fade-in">
                <div className="flex items-center space-x-2 mb-4">
                    <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">Editing:</span>
                    <span className="text-blue-700 font-bold">{activeTier} Tier</span>
                </div>

                {/* Base Service Costs */}
                <div className="mb-6 bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4 border-b pb-2">
                        <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                        <h4 className="text-sm font-bold text-gray-800">Base Service Costs</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* International Prices */}
                        <div className="space-y-3">
                            <label className="block text-xs font-bold text-blue-600 uppercase tracking-wide">
                                International Travelers (USD)
                            </label>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Adult Base</label>
                                    <div className="relative">
                                        <span className="absolute left-2 top-1.5 text-gray-400">$</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={currentVariant.baseServiceCosts[NationalityType.INTERNATIONAL]?.adult || 0}
                                            onChange={(e) => handleCostChange(NationalityType.INTERNATIONAL, 'adult', e.target.value)}
                                            className="w-full pl-6 pr-3 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Child Base</label>
                                    <div className="relative">
                                        <span className="absolute left-2 top-1.5 text-gray-400">$</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={currentVariant.baseServiceCosts[NationalityType.INTERNATIONAL]?.child || 0}
                                            onChange={(e) => handleCostChange(NationalityType.INTERNATIONAL, 'child', e.target.value)}
                                            className="w-full pl-6 pr-3 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Local Prices */}
                        <div className="space-y-3">
                            <label className="block text-xs font-bold text-orange-600 uppercase tracking-wide">
                                Local Residents (KES)
                            </label>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Adult Base</label>
                                    <div className="relative">
                                        <span className="absolute left-2 top-1.5 text-gray-400 text-xs font-bold">KSh</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={currentVariant.baseServiceCosts[NationalityType.LOCAL]?.adult || 0}
                                            onChange={(e) => handleCostChange(NationalityType.LOCAL, 'adult', e.target.value)}
                                            className="w-full pl-10 pr-3 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Child Base</label>
                                    <div className="relative">
                                        <span className="absolute left-2 top-1.5 text-gray-400 text-xs font-bold">KSh</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={currentVariant.baseServiceCosts[NationalityType.LOCAL]?.child || 0}
                                            onChange={(e) => handleCostChange(NationalityType.LOCAL, 'child', e.target.value)}
                                            className="w-full pl-10 pr-3 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-3 pt-3 border-t">
                        * <strong>Base Service Cost</strong> includes guide fees, vehicle, park fees (if fixed), and logistics. Hotel costs are calculated separately based on the selected Accommodation Pool.
                    </p>
                </div>

                {/* Logistics */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1 flex items-center gap-2">
                            <TruckIcon className="h-4 w-4 text-blue-500" />
                            Vehicle Type
                        </label>
                        <select
                            value={currentVariant.vehicleType}
                            onChange={(e) => handleInputChange('vehicleType', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            disabled={loadingLogistics}
                        >
                            {loadingLogistics ? (
                                <option>Loading transport...</option>
                            ) : (
                                <>
                                    {transportOptions.length === 0 && (
                                        <>
                                            <option value="Safari Minivan">Safari Minivan (Legacy)</option>
                                            <option value="4x4 Land Cruiser">4x4 Land Cruiser (Legacy)</option>
                                        </>
                                    )}
                                    {transportOptions.map(opt => (
                                        <option key={opt.id} value={opt.name}>{opt.name} ({opt.capacity} seats)</option>
                                    ))}
                                </>
                            )}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1 flex items-center gap-2">
                            <UserGroupIcon className="h-4 w-4 text-purple-500" />
                            Guide Ranking
                        </label>
                        <select
                            value={currentVariant.guideClass}
                            onChange={(e) => handleInputChange('guideClass', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            disabled={loadingLogistics}
                        >
                            {loadingLogistics ? (
                                <option>Loading rankings...</option>
                            ) : (
                                <>
                                    <option value="Non-guided">Non-guided (Self Drive)</option>
                                    {guideRankings.length === 0 && (
                                        <>
                                            <option value="Standard">Standard Driver-Guide (Legacy)</option>
                                            <option value="Silver">Silver Level Guide (Legacy)</option>
                                            <option value="Gold">Gold Level Guide (Legacy)</option>
                                        </>
                                    )}
                                    {guideRankings.map(rank => (
                                        <option key={rank.id} value={rank.name}>{rank.name}</option>
                                    ))}
                                </>
                            )}
                        </select>
                    </div>
                </div>

                {/* Inclusions & Exclusions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Inclusions */}
                    <div className="bg-white p-4 rounded-lg border border-green-100 shadow-sm">
                        <label className="block text-sm font-bold text-green-700 mb-3 border-b-2 border-green-100 pb-1">
                            Inclusions
                        </label>
                        <div className="space-y-2 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                            {currentVariant.inclusions.map((item, index) => (
                                <div key={`inc-${index}-${item.slice(0, 10)}`} className="flex gap-2">
                                    <input
                                        type="text"
                                        value={item}
                                        onChange={(e) => handleArrayChange('inclusions', index, e.target.value)}
                                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-green-500"
                                        placeholder="e.g., Park fees, Water"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeArrayItem('inclusions', index)}
                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                    >
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={() => addArrayItem('inclusions')}
                            className="mt-3 flex items-center text-sm font-medium text-green-600 hover:text-green-800 transition-colors"
                        >
                            <PlusIcon className="w-4 h-4 mr-1" />
                            Add Inclusion
                        </button>
                    </div>

                    {/* Exclusions */}
                    <div className="bg-white p-4 rounded-lg border border-red-100 shadow-sm">
                        <label className="block text-sm font-bold text-red-700 mb-3 border-b-2 border-red-100 pb-1">
                            Exclusions
                        </label>
                        <div className="space-y-2 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                            {currentVariant.exclusions.map((item, index) => (
                                <div key={`exc-${index}-${item.slice(0, 10)}`} className="flex gap-2">
                                    <input
                                        type="text"
                                        value={item}
                                        onChange={(e) => handleArrayChange('exclusions', index, e.target.value)}
                                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-red-500"
                                        placeholder="e.g., Tips, Alcohol"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeArrayItem('exclusions', index)}
                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                    >
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={() => addArrayItem('exclusions')}
                            className="mt-3 flex items-center text-sm font-medium text-red-600 hover:text-red-800 transition-colors"
                        >
                            <PlusIcon className="w-4 h-4 mr-1" />
                            Add Exclusion
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TierEditor;
// cache bust
